/**
 * Kaagazy's own GST invoices to the clinic: subscription charges and credit
 * top-ups. Not the clinic's patient invoices.
 *
 * PDFs are fetched as short-lived presigned URLs on click rather than up front,
 * so a list of 20 invoices does not mint 20 URLs the user will never open.
 */

import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { PlatformInvoiceAPI, type PlatformInvoice } from "../../services/billing.service";
import { SettingsCard } from "../../pages/settings/SettingsCard";

function formatPaise(paise: number): string {
    // Indian grouping: 10,00,000.00 rather than 1,000,000.00.
    const rupees = Math.floor(Math.abs(paise) / 100);
    const sub = Math.abs(paise) % 100;
    const grouped = rupees.toLocaleString("en-IN");
    const sign = paise < 0 ? "-" : "";
    return `${sign}₹${grouped}.${String(sub).padStart(2, "0")}`;
}

function formatDate(iso: string): string {
    return new Date(iso).toLocaleDateString("en-IN", {
        day: "2-digit", month: "short", year: "numeric",
    });
}

const REASON_LABEL: Record<string, string> = {
    subscription: "Subscription",
    credit_topup: "Credit top-up",
};

export const KaagazyInvoiceHistory: React.FC = () => {
    const [invoices, setInvoices] = useState<PlatformInvoice[]>([]);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        PlatformInvoiceAPI.list()
            .then(data => { if (!cancelled) setInvoices(data ?? []); })
            .catch(() => { if (!cancelled) setInvoices([]); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, []);

    const handleDownload = useCallback(async (inv: PlatformInvoice) => {
        setDownloading(inv.id);
        try {
            const { url } = await PlatformInvoiceAPI.getPdfUrl(inv.id);
            window.open(url, "_blank", "noopener,noreferrer");
        } catch {
            toast.error("Could not open the invoice. Please try again in a moment.");
        } finally {
            setDownloading(null);
        }
    }, []);

    return (
        <SettingsCard
            title="Kaagazy Invoices"
            subtitle="Tax invoices for your Kaagazy subscription and credit purchases"
        >
            {loading ? (
                <p className="text-sm text-gray-500">Loading…</p>
            ) : invoices.length === 0 ? (
                <p className="text-sm text-gray-500">
                    No invoices yet. Your first tax invoice appears here once your
                    subscription payment is processed.
                </p>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-xs font-medium text-gray-500 border-b border-gray-200">
                                <th className="py-2 pr-4">Invoice No</th>
                                <th className="py-2 pr-4">Date</th>
                                <th className="py-2 pr-4">For</th>
                                <th className="py-2 pr-4 text-right">Amount</th>
                                <th className="py-2 text-right">Document</th>
                            </tr>
                        </thead>
                        <tbody>
                            {invoices.map(inv => (
                                <tr key={inv.id} className="border-b border-gray-100 last:border-0">
                                    <td className="py-2.5 pr-4 font-medium text-gray-900">
                                        {inv.invoice_number}
                                        {inv.kind === "credit_note" && (
                                            <span className="ml-2 inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">
                                                Credit Note
                                            </span>
                                        )}
                                    </td>
                                    <td className="py-2.5 pr-4 text-gray-600">{formatDate(inv.issued_at)}</td>
                                    <td className="py-2.5 pr-4 text-gray-600">
                                        {REASON_LABEL[inv.reason] ?? inv.reason}
                                    </td>
                                    <td className="py-2.5 pr-4 text-right text-gray-900">
                                        {formatPaise(inv.total_paise)}
                                    </td>
                                    <td className="py-2.5 text-right">
                                        <button
                                            onClick={() => handleDownload(inv)}
                                            disabled={downloading === inv.id}
                                            className="text-blue-600 hover:text-blue-700 disabled:opacity-50 font-medium"
                                        >
                                            {downloading === inv.id ? "Opening…" : "Download"}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </SettingsCard>
    );
};
